import { useMemo } from "react";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import { useCurrentUser } from "src/dm/auth";
import UserName from "src/dm/components/UserName/UserName";
import * as s from "src/dm/pages/DealDetail/DealDetail.css";
import * as ls from "./DealHeaderPage.css";

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
};

const fmtMoney = (n: number | null): string =>
  n == null
    ? "—"
    : `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const fmtNum = (n: number | null): string =>
  n == null ? "—" : n.toLocaleString();

const fmtText = (v: string | null): string =>
  v == null || v === "" ? "—" : v;

interface RowProps {
  label: string;
  value: React.ReactNode;
}
const Row = ({ label, value }: RowProps) => (
  <div className={s.row}>
    <span className={s.rowLabel}>{label}</span>
    <span className={s.rowValue}>{value}</span>
  </div>
);

interface FlagProps {
  label: string;
  on: boolean;
  /**
   * Optional hover tooltip. audit/26-gap-closure-verification.md §3 item 6:
   * the Contractual Δ chip wanted a fuller explanation on hover than its
   * two-character label can convey. Forwarded verbatim to the rendered
   * <div> so any flag can grow a tooltip without further changes here.
   */
  title?: string;
}
const Flag = ({ label, on, title }: FlagProps) => (
  <div className={on ? s.flagOn : s.flagOff} title={title}>
    {on ? "✓ " : "○ "}
    {label}
  </div>
);

// Legacy DealType.Id → code map (per audit/06-runtime-inventory.md dropdown
// order on NewDeal.aspx). Used so we can gate rows on deal-type semantics
// instead of magic numbers scattered through the JSX.
//   1 Fly Sheet, 2 Wrap, 3 Circular, 4 TPR, 5 EDLC,
//   6 Run of Press (ROP), 7 Trade Deal, 8 Advertised Only
const DEAL_TYPE_WRAP_ID = 2;

const DealHeaderPage = () => {
  const deal = useDealContext();
  const { data: user } = useCurrentUser();

  // Per-deal-type + per-role visibility flags. Mirrors the row-by-row
  // `pnl*.Visible = …` toggles in legacy DealHeader.aspx.cs +
  // Controls/DealHeaderControl.ascx.cs (audit/11-aspxcs-and-business-nuances.md
  // §4 + audit/06-runtime-inventory.md).
  //
  // Wrap rows: visible only on Wrap deals (`MaxStoresInWrap`).
  // Allowance rows: visible only when the corresponding has*Allowance /
  //   allowanceReflectsToSixtyOne / isRewardPromotion flag is set.
  // Internal-only rows: hidden for external users (legacy `pnlInternalOnly`).
  // Daymon panel changes: legacy renders a different sidebar for Daymon users;
  //   that is gated in DealLayout.tsx — here we only need the role.
  const v = useMemo(() => {
    const isWrap = deal.dealTypeId === DEAL_TYPE_WRAP_ID;
    const isRewardPromo = deal.isRewardPromotion;
    const hasScanback = deal.hasScanbackAllowance;
    const hasBillBack = deal.hasBillBackAllowance;
    const hasCountRecount = deal.hasCountRecountAllowance;
    const reflectsTo61 = deal.allowanceReflectsToSixtyOne;
    const isExternalUser = user?.isExternalUser ?? false;
    const isInternalUser = user?.isInternalUser ?? !isExternalUser;

    return {
      showWrapRows: isWrap,
      showRewardRows: isRewardPromo,
      showScanbackRows: hasScanback,
      showBillBackRows: hasBillBack,
      showCountRecountRows: hasCountRecount,
      showReflectRows: reflectsTo61,
      // Legacy `pnlInternalOnly` rows hidden for external users. External
      // users still see the deal, but internal-only metadata (e.g. internal
      // fees, certain audit columns) is suppressed. Per audit §4 this is
      // limited to a small set — most fields stay visible to everyone.
      showInternalOnly: isInternalUser,
      // External-vendor liability section (parity with EditReward.tsx
      // `showVendorLiability`). Shown when user is external OR the deal
      // is not internal — gives the vendor a read-only view of liability
      // metadata on their own deals.
      showVendorLiability: isExternalUser || !deal.isInternalDeal,
    };
  }, [
    deal.dealTypeId,
    deal.isRewardPromotion,
    deal.hasScanbackAllowance,
    deal.hasBillBackAllowance,
    deal.hasCountRecountAllowance,
    deal.allowanceReflectsToSixtyOne,
    deal.isInternalDeal,
    user?.isExternalUser,
    user?.isInternalUser,
  ]);

  // Legacy DealHeader.aspx renders a prominent inline red banner near the
  // top of the form when a contractual field has changed since the last
  // vendor approval AND the deal is still pre-approval (status APPROVE or
  // AUTHORIZE — once the deal moves past authorisation the vendor has
  // already re-signed, so the warning is suppressed). audit/22-screenshot-
  // inventory.md and audit/26-gap-closure-verification.md §3 item 5.
  const showReapprovalBanner =
    deal.contractualFieldsChanged &&
    (deal.status === "APPROVE" || deal.status === "AUTHORIZE");

  return (
    <div>
      {showReapprovalBanner && (
        <div className={ls.reapprovedBanner} role="alert">
          ⚠ This deal must be reapproved by the vendor — contractual fields
          have changed since last approval.
        </div>
      )}

      <h2 style={{ marginTop: 0 }}>Deal Header</h2>

      <div className={s.sectionGrid}>
        <section className={s.card}>
          <h2 className={s.cardTitle}>Status</h2>
          <Row label="Status" value={fmtText(deal.status)} />
          <Row label="Status Date" value={fmtDate(deal.statusDate)} />
          {/* Resolve DOMAIN\username → display name via the shared
              UserName component. audit/21-visual-diff.md §12 cross-page. */}
          <Row label="Modified By" value={<UserName userId={deal.dealStatModifiedBy} />} />
          <Row label="Promo Advisor" value={fmtText(deal.promotionAdvisorStatus)} />
          <Row label="Notes" value={fmtNum(deal.noteCount)} />
          <Row label="Errors" value={fmtNum(deal.errorCount)} />
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Ad &amp; Promotion Dates</h2>
          <Row label="Ad Start" value={fmtDate(deal.adStartDate)} />
          <Row label="Ad End" value={fmtDate(deal.adEndDate)} />
          <Row label="Promo Start" value={fmtDate(deal.promoStartDate)} />
          <Row label="Promo End" value={fmtDate(deal.promoEndDate)} />
          <Row label="Best History" value={fmtDate(deal.bestHistoryDate)} />
          <Row label="Ad Plan" value={fmtText(deal.adPlanCode)} />
          <Row label="Ad Strength" value={fmtText(deal.adStrength)} />
          <Row label="Ad Block" value={fmtNum(deal.adBlockId)} />
          <Row label="Feature Seq" value={fmtNum(deal.adFeatureSequence)} />
          <Row label="Signage End" value={fmtDate(deal.signageEndDate)} />
          {/* Reward-promo dates — legacy `chk_Reward` row pair hidden unless
              RewardPromotionFlag === "Y". DEAL.RWRD_PROM_STRT/END_DTE. */}
          {v.showRewardRows && (
            <>
              <Row
                label="Reward Start"
                value={fmtDate(deal.rewardPromotionStartDate)}
              />
              <Row
                label="Reward End"
                value={fmtDate(deal.rewardPromotionEndDate)}
              />
            </>
          )}
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Allowance Dates</h2>
          {/* Scanback row pair — legacy `chk_scanback` toggle. Hidden unless
              the deal has a scanback allowance. */}
          {v.showScanbackRows && (
            <>
              <Row label="Scanback Start" value={fmtDate(deal.scanbackStartDate)} />
              <Row label="Scanback End" value={fmtDate(deal.scanbackEndDate)} />
            </>
          )}
          {/* Reflect-to-61 row pair — only relevant when the allowance
              reflects to GL account 61 (`ReflectToSixtyOneFlag`). */}
          {v.showReflectRows && (
            <>
              <Row label="Reflect Start" value={fmtDate(deal.reflectStartDate)} />
              <Row label="Reflect End" value={fmtDate(deal.reflectEndDate)} />
            </>
          )}
          {/* Count/Recount row pair — only when `CountRecountFlag === "Y"`. */}
          {v.showCountRecountRows && (
            <>
              <Row label="Count/Recount Start" value={fmtDate(deal.countRecountStartDate)} />
              <Row label="Count/Recount End" value={fmtDate(deal.countRecountEndDate)} />
            </>
          )}
          {/* When all allowance flags are off the card would render empty.
              Show a small hint so the user understands why dates are missing
              instead of seeing an empty card with just a title. */}
          {!v.showScanbackRows &&
            !v.showReflectRows &&
            !v.showCountRecountRows && (
              <Row label="—" value={<em style={{ color: "#94a3b8" }}>No allowance dates apply to this deal.</em>} />
            )}
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Order &amp; Shipment</h2>
          <Row label="First Order" value={fmtDate(deal.firstOrderDate)} />
          <Row label="Last Order" value={fmtDate(deal.lastOrderDate)} />
          {/* BillBack-only ship dates: in legacy, First/Last Ship rows live
              next to the BillBack checkbox and only matter when the deal has
              a bill-back allowance. */}
          {v.showBillBackRows && (
            <>
              <Row label="First Shipment" value={fmtDate(deal.firstShipmentDate)} />
              <Row label="Last Shipment" value={fmtDate(deal.lastShipmentDate)} />
            </>
          )}
          {/* Audit Date is part of `pnlInternalOnly` — hidden for external
              users (audit/11-aspxcs-and-business-nuances.md §4 line 84). */}
          {v.showInternalOnly && (
            <Row label="Audit Date" value={fmtDate(deal.auditDate)} />
          )}
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Quantities &amp; Cost</h2>
          <Row label="Projected Units" value={fmtNum(deal.projectedUnitSales)} />
          <Row label="UOM" value={fmtText(deal.projectedUnitsUom)} />
          <Row label="Min Vendor Purchase" value={fmtNum(deal.minimumPurchaseQuantity)} />
          <Row label="Max Promo Qty" value={fmtNum(deal.promoMaxQty)} />
          {/* `MaxStoresInWrap` only makes sense for Wrap deals — legacy
              hides `txtMaxWrapStores` for any other deal type. */}
          {v.showWrapRows && (
            <Row label="Stores in Wrap" value={fmtNum(deal.maxStoreCountAllowedForWrap)} />
          )}
          <Row label="Deal Cost" value={fmtMoney(deal.dealCost)} />
          <Row label="Other Charges" value={fmtMoney(deal.otherCharges)} />
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Fees</h2>
          <Row label="Advertising" value={fmtMoney(deal.advertisingFee)} />
          <Row label="Administration" value={fmtMoney(deal.administrationFee)} />
          <Row label="Fly Sheet" value={fmtMoney(deal.flySheetFee)} />
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Vendor &amp; Linkage</h2>
          <Row label="Vendor / Broker" value={fmtNum(deal.vendorBrokerId)} />
          <Row label="Link" value={fmtNum(deal.linkId)} />
          <Row label="Deal Type" value={fmtNum(deal.dealTypeId)} />
          <Row label="Country of Origin" value={fmtText(deal.countryOfOrigin)} />
          <Row label="3rd-Party Notify" value={fmtText(deal.thirdPartyNotification)} />
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Flags</h2>
          <div className={s.flagsList}>
            <Flag label="Locked" on={deal.isLocked} />
            <Flag label="Requires Action" on={deal.isActionRequired} />
            <Flag label="Internal" on={deal.isInternalDeal} />
            <Flag label="Demo" on={deal.isDemo} />
            <Flag label="Floor Stock" on={deal.isFloorStockProtected} />
            <Flag label="Excl. CMRKT" on={deal.communityMarketExcluded} />
            <Flag label="Off Invoice" on={deal.hasOffInvoiceAllowance} />
            <Flag label="Bill Back" on={deal.hasBillBackAllowance} />
            <Flag label="Count/Recount" on={deal.hasCountRecountAllowance} />
            <Flag label="Pass Thru" on={deal.hasPassThruAllowance} />
            <Flag label="Scanback" on={deal.hasScanbackAllowance} />
            <Flag label="Reflect-to-61" on={deal.allowanceReflectsToSixtyOne} />
            <Flag label="Reward Promo" on={deal.isRewardPromotion} />
            <Flag label="Secondary Promo" on={deal.isSecondaryPromotion} />
            <Flag label="Loaded" on={deal.dealHasBeenLoaded} />
            <Flag
              label="Contractual Δ"
              on={deal.contractualFieldsChanged}
              title="Contractual fields changed since last approval — vendor re-signature may be required"
            />
            <Flag label="Notify on Expire" on={deal.sendExpirationNotification} />
            <Flag label="GEAC" on={deal.isGeac} />
            <Flag label="Term Sheet" on={deal.isTermSheet} />
            <Flag label="No-Save Price" on={deal.suppressSavePriceFormat} />
            <Flag label="Wine Sav-Up-To" on={deal.wineSaveUpTo} />
            <Flag label="Excl. AMP" on={deal.excludedFromAmp} />
          </div>
        </section>

        {/* External-vendor liability section. Parity with EditReward.tsx
            (`showVendorLiability = isExternalUser || !deal.isInternalDeal`).
            Read-only summary so vendors see who owes what on their own deals
            without exposing internal accounting. */}
        {v.showVendorLiability && (
          <section className={s.card}>
            <h2 className={s.cardTitle}>Vendor Liability</h2>
            <Row label="Vendor / Broker" value={fmtNum(deal.vendorBrokerId)} />
            <Row label="Off Invoice" value={deal.hasOffInvoiceAllowance ? "Yes" : "No"} />
            <Row label="Bill Back" value={deal.hasBillBackAllowance ? "Yes" : "No"} />
            <Row label="Scanback" value={deal.hasScanbackAllowance ? "Yes" : "No"} />
            <Row label="Pass Thru" value={deal.hasPassThruAllowance ? "Yes" : "No"} />
            <Row label="Reflect-to-61" value={deal.allowanceReflectsToSixtyOne ? "Yes" : "No"} />
          </section>
        )}
      </div>

      <div className={s.sectionGrid}>
        <section className={s.card}>
          <h2 className={s.cardTitle}>Consumer Message</h2>
          <div className={s.commentBlock}>{fmtText(deal.consumerMessageComment)}</div>
        </section>
        <section className={s.card}>
          <h2 className={s.cardTitle}>Coupon Comment</h2>
          <div className={s.commentBlock}>{fmtText(deal.couponComment)}</div>
        </section>
        <section className={s.card}>
          <h2 className={s.cardTitle}>AMP — Store</h2>
          <div className={s.commentBlock}>{fmtText(deal.ampStoreComment)}</div>
        </section>
        <section className={s.card}>
          <h2 className={s.cardTitle}>AMP — Merchandising</h2>
          <div className={s.commentBlock}>{fmtText(deal.ampMerchandisingComment)}</div>
        </section>
        <section className={s.card}>
          <h2 className={s.cardTitle}>AMP — Advertising</h2>
          <div className={s.commentBlock}>{fmtText(deal.ampAdvertisingComment)}</div>
        </section>
        <section className={s.card}>
          <h2 className={s.cardTitle}>Signage Notes</h2>
          <div className={s.commentBlock}>{fmtText(deal.signageNotes)}</div>
        </section>
      </div>
    </div>
  );
};

export default DealHeaderPage;
